// База знаний бота о картах: чем карта полезна сама по себе и с чем она синергирует.
// Движок карты по тексту не читает — здесь их ценность записана вручную (см. docs/bot-synergy.md — отчёт по этой таблице).
//
// Модель. Ценность карты измеряется в «мощи за розыгрыш» (P): 1 P = 1 мощь. Очки (ПО) считаются отдельно.
// Синергии описаны через темы: карта «даёт» тему (provides) и «хочет» тему (wants: сколько P за единицу темы в колоде).
// Тема бывает двух охватов: 'hand' — важно оказаться в одной руке (твари под контролем, палочки, урон);
// 'owned' — важно просто владеть (легенды у Гусыни, жетоны дохлых колдунов, вялые палочки у Виагруса).
// Постоянки всегда «в руке» (в игре), поэтому их вклад в hand-темы не масштабируется.
// wantsVp — то же, но платит очками в конце игры (Гусыня: +2 ПО за легенду; Гикпиг: +1 ПО за тварь).
import type { BossId, CardDef, CardId, CardInst, Game, Player, TraitKey } from '../types.ts';
import { card, ownedCards, hasTag, traitOf, ddkDef, isLegend } from './state.ts';
import { RULES } from './rules.ts';
import { handSizeOf } from './mutators.ts';
import { BOSSES } from './bosses.ts';
import * as H from './hooks.ts';
import { registerModeHook } from './rebalance.ts';

export type Theme =
  | 'beast' // тварь под контролем (в одной руке)
  | 'beastOwned' // тварь в колоде (Гикпиг)
  | 'ddk' // жетоны дохлых колдунов у игрока (включая дохляков-постоянок)
  | 'wand' // палочка в руке
  | 'legend' // легенда под контролем (в одной руке: Виабу)
  | 'legendOwned' // легенда во владении (Гусыня)
  | 'permanent' // постоянки в игре
  | 'damage' // атаки уроном (единица ≈ 7 урона)
  | 'expensive' // дорогая карта под контролем (Тапки, Трондец)
  | 'wide' // много карт под контролем (Надёжный план)
  | 'power' // много мощи в ходу (Лохолох; единица ≈ 2 мощи)
  | 'loser' // игрок — лошара (Цирк, Лошашлык)
  | 'limp' // вялые палочки во владении (Виагрус)
  | 'chips' // чипсины (единица ≈ 1 чипсина; есть у игрока или приносят карты)
  | 'treasure' // сокровища во владении (жетон свойства)
  | 'wizardSpell'; // волшебник/заклинание под контролем (жетон свойства ☀)

export type Scope = 'hand' | 'owned';
export const THEME_SCOPE: Record<Theme, Scope> = {
  beast: 'hand', beastOwned: 'owned', ddk: 'owned', wand: 'hand', legend: 'hand', legendOwned: 'owned', permanent: 'owned',
  damage: 'hand', expensive: 'hand', wide: 'hand', power: 'hand', loser: 'owned', limp: 'owned', chips: 'owned',
  treasure: 'owned', wizardSpell: 'hand',
};
export const THEMES = Object.keys(THEME_SCOPE) as Theme[];

export type Wants = Partial<Record<Theme, number>>;

/** Знания о карте (всё, что не выводится из структурных полей CardDef) */
export interface CardInfo {
  /** ожидаемый урон одной цели за розыгрыш (до защиты); all — каждому врагу, lr — левому и правому */
  dmg?: number;
  all?: boolean | 'lr';
  unavoidable?: boolean;
  draw?: number;
  chips?: number;
  heal?: number;
  /** ПО, которые карта отнимает у врага за розыгрыш (вялые палочки, жетоны, лошара); all — у каждого */
  vpEnemy?: number;
  /** ценность защиты сверх базовой (добор, чипсины, перенаправление); отрицательная — защита с платой */
  def?: number;
  /** постоянка: мощь (или её эквивалент) каждый ход */
  perTurn?: number;
  /** постоянка: ПО каждый ход (Виагрус берёт палочку в плюс) */
  vpPerTurn?: number;
  /** прочая ценность за розыгрыш в P (гибкие эффекты, ускорение, скидки) */
  xtra?: number;
  /** ПО в конце игры сверх звезды (Сердце мага +2; Вялая башня −2 за палочки) */
  vpExtra?: number;
  wants?: Wants;
  /** предел учёта единиц темы для wants (условие «хотя бы ещё 1 тварь» — 1) */
  caps?: Partial<Record<Theme, number>>;
  wantsVp?: Wants;
  provides?: Wants;
  /** особая оценка по состоянию колоды (Смерть от холестерина: +7 мощи без жетонов, иначе −1 жетон) */
  custom?: (prof: DeckProfile) => { p?: number; vp?: number };
}

const c = (info: CardInfo): CardInfo => info;
/**
 * Базовая ценность «избежать атаки» (без бонусов). По журналам 105 партий ботов защита сама по себе стоит мало
 * (Берсерк, Близнец, Бесопанки, Стаканчик — покупатель в минусе), ценны бонусы: добор, чипсины, перенаправление.
 */
export const DEF_BASE = 0.9;
/** Обычная защита «сбрось, чтобы избежать» уже учтена базой; здесь — добавка за добор/чипсины/перенаправление */
const DEF_DRAW = 0.6;
const DEF_REDIRECT = 1.6;

export const INFO: Record<CardId, CardInfo> = {
  // --- затравки, шальная магия, вялые палочки ---
  'znak': c({}),
  'pshik': c({}),
  'vyalaya-palochka': c({}),
  'syrnaya-palochka': c({ dmg: 1, xtra: 0.3 }),
  'palochka-hrenalochka': c({ dmg: 1, xtra: 0.3 }),
  'shalnaya-magiya': c({ xtra: 1.6 }),
  // --- легенды ---
  'gusynya-koroleva-mat': c({ wantsVp: { legendOwned: 2 } }),
  'ta-samaya-vyalaya-palochka': c({ dmg: 7, xtra: 1 }),
  'malchishnik-v-rlyehe': c({ draw: 2, dmg: 6, all: true, unavoidable: true, xtra: -0.5 }),
  'zhnitsa-lyubvi': c({ wantsVp: { ddk: 3 }, caps: { ddk: 1 } }),
  'venerina-magolovka': c({ xtra: 1.5 }),
  'buzyashchaya-palochka-garika-potnogo': c({ dmg: 10 }),
  'mortal-kombo': c({ dmg: 25 }),
  'ultimativnoe-tronado': c({ wants: { damage: 5 }, caps: { damage: 2 } }),
  'trondets': c({ perTurn: 2, wants: { expensive: 1.5 } }),
  'edinstvennyy-v-mire-loholoh': c({ wants: { power: 1.5 } }),
  'bashnya-radosti': c({}),
  'nekrofeya-gnilyusya': c({ wants: { ddk: 3.5 } }),
  'dermak-gastrit': c({ dmg: 12, all: true, xtra: 0.5 }),
  'vosstanie-iz-zada': c({ draw: 2, xtra: 1, wants: { damage: 1 } }),
  'park-vurdalakcionov': c({ perTurn: 0.5, wants: { damage: 1.2 } }),
  'sdelka-so-zhgunom': c({ xtra: 2.5 }),
  'volshebnyj-krolik': c({ draw: 3, dmg: 20, all: true }),
  'palochka-ushatalochka': c({ dmg: 1, xtra: 2 }),
  'pohotlivyj-mayachok': c({ wants: { ddk: 1 } }),
  'bratya-loshashnye': c({ def: 2 * DEF_DRAW + 0.5 }),
  'naryvka-koroleva-genitalnyh-garpij': c({ vpEnemy: 2, all: true }),
  'zad-v-budushchee': c({ xtra: 2 }),
  'hahataler-zloradnik': c({ vpEnemy: 6 }),
  'kapitan-bartolomyau': c({ xtra: 2 }),
  'serdce-maga-po-epichnomu': c({ draw: 1, chips: 3, vpExtra: 2 }),
  'smert-ot-holesterina': c({ custom: (prof) => (prof.ddk === 0 ? { p: 7 } : prof.realDdk ? { vp: 3 } : {}) }),
  'legendohranitel': c({ draw: 2, chips: 2, def: 2 * DEF_DRAW + 1.2 }),
  'palochka-miniganochka': c({ dmg: 24 }),
  'chipsihoz-arena': c({ wants: { damage: 3 } }),
  'viagrus-tvyordyj-lord': c({ perTurn: 3, vpPerTurn: 1, wants: { limp: 1.5 }, wantsVp: { limp: 2 } }),
  'nadyozhnyj-plan': c({ draw: 2, wants: { wide: 0.5 }, caps: { wide: 3 } }),
  'brataliti': c({ xtra: 1 }),
  'orbitalnaya-evonnaya-pushka': c({ dmg: 10, all: 'lr' }),
  'avada-loshavra': c({ draw: 2, vpEnemy: 5, xtra: 0.5 }),
  // --- основная колода ---
  'spinogryzogryzy': c({ wants: { beast: 1.8 }, caps: { beast: 1 } }), // прореживание каждый розыгрыш — по журналам одна из лучших дешёвых карт
  'zloshirak': c({}),
  'priunyvshij-ork': c({ wants: { beast: 2 }, caps: { beast: 1 } }),
  'bliznec-bubenec': c({ def: 0.8 }),
  'vyalaya-bashnya': c({ perTurn: 1, vpExtra: -2, provides: { limp: 2 } }),
  'ispanskij-styd': c({ wants: { beast: 4 }, caps: { beast: 1 }, provides: { damage: 1.3 } }),
  'palochka-losharochka': c({ dmg: 5, xtra: 0.7 }),
  'epichnyj-merch-boevykh-magov': c({ xtra: 1.2 }),
  'chleny-bandy': c({ dmg: 3, all: true }),
  'pushka-blevushka': c({ xtra: 1.5, provides: { damage: 0.5 } }),
  'chyornaya-ovtsa-chashchob': c({ dmg: 12 }),
  'sladkiy-smurfinyor': c({ dmg: 4, heal: 4 }),
  'berserk-shchelkunchik': c({ dmg: 6, def: DEF_DRAW }),
  'besopanki': c({ draw: 1, def: DEF_DRAW + 0.6 }),
  'povelitel-shkurok': c({ vpEnemy: 1 }),
  'surkovyy-agent': c({ xtra: -0.8 }), // уничтожает 3 карты основной колоды (приближает конец) и играет беспредел по всем
  'lampovoe-zhelanie': c({ def: 0.8 }),
  'stakanchik-iz-tsarbaksa': c({ def: -0.6, wants: { chips: 0.3 }, caps: { chips: 1 } }),
  'palochka-shlyopalochka': c({ dmg: 2, chips: 2 }),
  'bratstvo-kontsa': c({ draw: 1, xtra: -0.4 }),
  'palochka-chipsalochka': c({ wants: { chips: 3 }, caps: { chips: 1 }, provides: { damage: 1 } }),
  'tapki-donatki': c({ dmg: 4, wants: { expensive: 2 }, caps: { expensive: 3 } }),
  'sosochnyy-prokolist': c({ dmg: 7, all: true }),
  'loshashlyk': c({ xtra: 0.5, wants: { loser: 0.9 } }),
  'nekroshest': c({ wants: { ddk: 2 } }),
  'tsirk-bratev-loshashnykh': c({ wants: { loser: 2 }, wantsVp: { loser: 15 } }),
  'marmeladnyy-arkhimag': c({ custom: (prof) => (prof.loser && !prof.circus ? { vp: 4, p: 1 } : { p: 1 }) }),
  'govna-piroga': c({ xtra: 2.5 }),
  'volshebnye-otkhody': c({ xtra: 2 }),
  'zhelatinovyy-sisyak': c({ perTurn: 1 }),
  'volchiy-zhor': c({ dmg: 4, xtra: 0.5 }),
  'vurdalakar': c({ xtra: 0.8 }),
  'shlem-totem': c({ wants: { chips: 0.8 }, caps: { chips: 1 } }),
  'nechistaya-sila': c({ draw: 1, dmg: 7, all: 'lr' }),
  'mozgoshtorm': c({ draw: 2 }),
  'pivohranilishche': c({}),
  'zak-dohlyak': c({ wants: { ddk: 0.4 }, provides: { ddk: 1 } }),
  'chak-dohlyak': c({ wants: { ddk: 0.4 }, provides: { ddk: 1 } }),
  'zhak-dohlyak': c({ wants: { ddk: 0.4 }, provides: { ddk: 1 } }),
  'ermak-dohlyak': c({ wants: { ddk: 0.4 }, provides: { ddk: 1 } }),
  'isaak-dohlyak': c({ wants: { ddk: 0.4 }, provides: { ddk: 1 } }),
  'musornyj-veter': c({ xtra: 1, dmg: 5, all: true }),
  'zhidkaya-kara': c({ dmg: 7, xtra: 0.2 }),
  'sherif-muhtar': c({ draw: 2, def: 2 * DEF_DRAW }),
  'glistomag': c({ def: 1.2 }),
  'sukkulyondra': c({ wants: { beast: 0.9 }, caps: { beast: 1 } }),
  'idol-schastlivoj-suchki': c({ def: DEF_DRAW + 0.6 }),
  'zolotoj-parashyut': c({ def: 1.2 }),
  'vedmin-podarochek': c({ draw: 1, wants: { permanent: 1 } }),
  'dlan-tvortsa': c({ perTurn: 1.2 }),
  'baklazhaba': c({ xtra: 3 }),
  'smazochnaya-gryaznaya-palka': c({ wants: { wand: 2 } }),
  'suvenirnyj-laryok-v-zale-legend': c({ xtra: 0.5 }),
  'koldunator': c({}),
  'potnyj-gikpig': c({ wantsVp: { beastOwned: 1 } }),
  'zhrangutan': c({ xtra: 3, wants: { beast: 3 } }),
  'razvratot': c({ xtra: 2.7, wants: { beast: 2.7 }, provides: { damage: 1 } }),
  // --- фамильяры ---
  'malchik-kotoryj-vyzhral': c({ def: 0.3, wants: { wand: 0.5 } }),
  'poehavshij-notari-us': c({ def: 1.5 }),
  'zozhlozhniki': c({ xtra: 0.5, def: DEF_DRAW + 1.4 }),
  'muertito-knizhnyj-cherv': c({ draw: 1, wants: { ddk: 1 }, def: DEF_DRAW + 0.6 }),
  'benzopila-druzhok': c({ wants: { damage: 1 }, def: DEF_DRAW + DEF_REDIRECT }),
  'myzhsemya': c({ draw: 1, xtra: 1.2, def: 2 * DEF_DRAW }),
  'schastlivyj-viabu': c({ wants: { legend: 3.6 }, caps: { legend: 1 }, def: DEF_DRAW }),
  'konduktor-zhmi-na-tormoza': c({ draw: 2, def: DEF_DRAW + DEF_REDIRECT, provides: { beast: 1, wizardSpell: 1 } }),
  'salnyj-shut': c({ vpEnemy: 1, def: DEF_DRAW + DEF_REDIRECT }),
  'nikchemuhazhery': c({ draw: 1, def: 0.8 }),
};

/** Знания о жетонах свойств: что они «хотят» от колоды (в P за единицу темы) */
export const TRAIT_INFO: Record<TraitKey, { wants?: Wants; wantsVp?: Wants; caps?: Partial<Record<Theme, number>> }> = {
  treasureDiscount: { wants: { treasure: 0.3 }, wantsVp: { treasure: 1 } },
  wizardChip: {},
  hrenalochka: { wants: { wand: 1 } },
  permChip: {},
  prize: {},
  act6: { wants: { beast: 0.5, treasure: 0.5 }, caps: { beast: 2, treasure: 2 } },
  spellHand: {},
  beastTop: {},
  threeFamiliars: {},
  act10: { wants: { wizardSpell: 0.7 }, caps: { wizardSpell: 2 } },
};

/** Разовая добавка за получение карты этого типа (жетоны «получив волшебника — чипсина» и т.п.) */
/**
 * Пары свойство × карта из контрфактики покупок (tools/branches.ts, агрегация branches-report --traits:
 * 5233 точки, 75 партий; только пары с |лифт доли побед| ≥ 2σ и n ≥ 12). Бонус в P ≈ лифт × 12.
 * Для измеренной карты перекрывает типовую эвристику ниже (замер уже включает всё, что она угадывала).
 */
const TRAIT_CARDS: Partial<Record<TraitKey, Record<CardId, number>>> = {
  prize: {
    'malchik-kotoryj-vyzhral': 1.2, 'vosstanie-iz-zada': 1, 'nekrofeya-gnilyusya': 0.9, 'nechistaya-sila': 0.9,
    'volshebnye-otkhody': -0.4, 'pushka-blevushka': -0.4, 'palochka-shlyopalochka': -0.6,
  },
  act10: { 'volshebnye-otkhody': 0.8, 'povelitel-shkurok': 0.7, 'surkovyy-agent': -0.6, 'nekroshest': -0.7, koldunator: -0.9 },
  threeFamiliars: { 'pushka-blevushka': 0.4, glistomag: -0.4, 'buzyashchaya-palochka-garika-potnogo': -0.9 },
  act6: { sukkulyondra: -0.3, 'lampovoe-zhelanie': -0.6 },
  hrenalochka: { 'shlem-totem': -0.5 },
  treasureDiscount: { 'bliznec-bubenec': -0.6 },
  wizardChip: { pivohranilishche: -0.8, 'zolotoj-parashyut': -0.9, brataliti: -1.3 },
};

export function traitGainBonus(key: TraitKey | null, def: CardDef): number {
  if (!key) return 0;
  const measured = TRAIT_CARDS[key]?.[def.id];
  if (measured != null) return measured;
  switch (key) {
    case 'wizardChip': return def.type === 'волшебник' ? 0.9 : 0;
    case 'spellHand': return def.type === 'заклинание' ? 0.5 : 0;
    case 'beastTop': return def.type === 'тварь' ? 0.7 : 0;
    case 'permChip': return def.permanent ? 1.2 : 0;
    default: return 0;
  }
}

/** Ручные поправки: пары карт, чью связку тематическая модель не видит или недооценивает */
export type Tier = 'S' | 'A' | 'B' | 'C' | 'D';
export const TIER_VALUE: Record<Tier, number> = { S: 6, A: 4, B: 2.5, C: 1.5, D: 0.7 };
export const PAIRS: [CardId, CardId, Tier][] = [
  ['benzopila-druzhok', 'palochka-ushatalochka', 'S'], // неизбежная атака + мгновенная победа
  ['vosstanie-iz-zada', 'palochka-ushatalochka', 'S'],
  ['vosstanie-iz-zada', 'mortal-kombo', 'A'],
  ['benzopila-druzhok', 'mortal-kombo', 'A'],
  ['chipsihoz-arena', 'volshebnyj-krolik', 'S'],
  ['ultimativnoe-tronado', 'volshebnyj-krolik', 'S'],
  ['chipsihoz-arena', 'palochka-miniganochka', 'A'],
  ['smazochnaya-gryaznaya-palka', 'palochka-miniganochka', 'S'], // 4 выстрела: +8 урона и +1 мощь
  ['smazochnaya-gryaznaya-palka', 'malchik-kotoryj-vyzhral', 'A'],
  ['avada-loshavra', 'tsirk-bratev-loshashnykh', 'A'], // самому стать лошарой с Цирком выгодно
  ['baklazhaba', 'tsirk-bratev-loshashnykh', 'B'],
  ['brataliti', 'mortal-kombo', 'B'],
  ['zad-v-budushchee', 'epichnyj-merch-boevykh-magov', 'C'],
  ['edinstvennyy-v-mire-loholoh', 'zhrangutan', 'A'],
  ['edinstvennyy-v-mire-loholoh', 'nekroshest', 'B'],
  ['edinstvennyy-v-mire-loholoh', 'sdelka-so-zhgunom', 'B'],
  ['park-vurdalakcionov', 'dlan-tvortsa', 'C'],
  ['tapki-donatki', 'trondets', 'B'],
  ['nekrofeya-gnilyusya', 'pohotlivyj-mayachok', 'B'],
  ['zhnitsa-lyubvi', 'hahataler-zloradnik', 'C'],
];
const pairKey = (a: CardId, b: CardId): string => (a < b ? `${a}|${b}` : `${b}|${a}`);
const PAIR_MAP: Record<string, number> = {};
for (const [a, b, t] of PAIRS) PAIR_MAP[pairKey(a, b)] = TIER_VALUE[t];
export function pairValue(a: CardId, b: CardId): number {
  return PAIR_MAP[pairKey(a, b)] ?? 0;
}

// ---------- профиль колоды ----------

export interface DeckProfile {
  /** число карт в колоде (без постоянок в игре) */
  n: number;
  /** средняя ценность карты колоды в P */
  avg: number;
  /** средняя мощь карты (только поле power) — для оценки «сколько мощи в ходу» */
  avgPower: number;
  /** мощь и её эквиваленты, которые постоянки в игре дают каждый ход */
  permPerTurn: number;
  /** ожидаемая мощь за ход: рука × средняя мощь + постоянки */
  turnPower: number;
  /** базовый размер руки партии (5, с мутатором «Жирная рука» — 6) */
  hand: number;
  /** босс-владелец колоды: его пассивка меняет ценность карт (bossCardBonus) */
  boss: BossId | null;
  supply: Record<Theme, number>;
  demand: Record<Theme, number>;
  demandVp: Record<Theme, number>;
  ddk: number;
  /** настоящих жетонов (не дохляков) */
  realDdk: number;
  loser: boolean;
  circus: boolean;
  chips: number;
  legendsOwned: number;
  traitKey: TraitKey | null;
  players: number;
  /** id карт во владении (для парных поправок) */
  cids: CardId[];
}

const zeroThemes = (): Record<Theme, number> => {
  const r = {} as Record<Theme, number>;
  for (const t of THEMES) r[t] = 0;
  return r;
};

function infoOf(def: CardDef): CardInfo {
  return INFO[def.id] ?? {};
}

/** Что карта даёт по темам (структурные признаки + таблица) */
export function providesOf(def: CardDef, forPlayer: Player | null): Wants {
  const info = infoOf(def);
  const w: Wants = { ...(info.provides ?? {}) };
  const add = (t: Theme, v: number): void => { w[t] = (w[t] ?? 0) + v; };
  if (def.type === 'тварь') { add('beast', 1); add('beastOwned', 1); }
  if (def.type === 'сокровище') add('treasure', 1);
  if (def.type === 'волшебник' || def.type === 'заклинание') add('wizardSpell', 1);
  if (hasTag(def.id, 'wand')) add('wand', 1);
  if (def.legend || (forPlayer && H.familiarsAreLegends(forPlayer) && def.type === 'фамильяр')) { add('legend', 1); add('legendOwned', 1); }
  if (def.permanent) add('permanent', 1);
  if (def.type === 'вялая палочка') add('limp', 1);
  if (hasTag(def.id, 'deadWizard')) add('ddk', 1);
  if (info.dmg) {
    const mult = info.all === true ? 2 : info.all === 'lr' ? 1.6 : 1;
    add('damage', Math.min(4, (info.dmg * mult) / 7));
  }
  if (info.chips) add('chips', info.chips);
  if ((def.cost ?? 0) >= 7) add('expensive', ((def.cost ?? 0) - 4) / 4);
  add('wide', 0.2 + (info.draw ?? 0) * 0.2);
  add('power', def.power / 2 + (info.perTurn ?? 0) / 2);
  return w;
}

/** Масштаб вклада карты в hand-тему: постоянка всегда в игре, обычная карта — с вероятностью попасть в ту же руку */
function handScale(def: CardDef, n: number): number {
  return def.permanent ? 1 : Math.min(1, (RULES.handSize - 1) / Math.max(RULES.handSize, n));
}

/**
 * Профиль колоды с кэшем: в примерках бота одна и та же колода считается сотни раз. Ключ — всё, от чего зависит
 * профиль: состав карт (мультимножество), жетон свойства, лошара, чипсины, жетоны ЖДК, число игроков.
 */
const PROFILE_CACHE = new Map<string, DeckProfile>();
export function deckProfile(G: Game, p: Player): DeckProfile {
  const owned = ownedCards(G, p);
  const ids = new Array<string>(owned.length);
  for (let i = 0; i < owned.length; i++) ids[i] = owned[i]?.cid ?? '';
  ids.sort();
  const key = `${p.traitId ?? ''}|${p.boss ?? ''}|${p.loser ? 1 : 0}|${p.chips}|${p.ddk.join(',')}|${G.players.length}|${handSizeOf(G)}|${p.permanents.filter((x) => hasTag(x, 'deadWizard')).length}|${ids.join(',')}`;
  const hit = PROFILE_CACHE.get(key);
  if (hit) return hit;
  const prof = computeDeckProfile(G, p, owned);
  if (PROFILE_CACHE.size > 5000) PROFILE_CACHE.clear();
  PROFILE_CACHE.set(key, prof);
  return prof;
}

/** Профиль без кэша (для проверок) */
export function deckProfileUncached(G: Game, p: Player): DeckProfile {
  return computeDeckProfile(G, p, ownedCards(G, p));
}

function computeDeckProfile(G: Game, p: Player, owned: CardInst[]): DeckProfile {
  const n = Math.max(1, owned.filter((x) => !card(x).permanent).length);
  const supply = zeroThemes();
  const demand = zeroThemes();
  const demandVp = zeroThemes();
  const traitKey = traitOf(p)?.key ?? null;
  const cids: CardId[] = [];
  let powerSum = 0;
  let permPerTurn = 0;
  let realDdk = p.ddk.length;
  for (const id of p.ddk) if (ddkDef(id).key === 'k20') demand['wand'] += 1.8;
  for (const x of owned) {
    const def = card(x);
    const info = infoOf(def);
    cids.push(def.id);
    if (!def.permanent) powerSum += def.power;
    if (def.permanent && (info.perTurn || def.id === 'pohotlivyj-mayachok')) permPerTurn += info.perTurn ?? 0;
    const hs = handScale(def, n);
    const prov = providesOf(def, p);
    for (const t of THEMES) {
      const v = prov[t];
      if (v) supply[t] += THEME_SCOPE[t] === 'hand' ? v * hs : v;
    }
    for (const t of THEMES) {
      const w = info.wants?.[t];
      if (w) demand[t] += THEME_SCOPE[t] === 'hand' && !def.permanent ? w * hs : w;
      const wv = info.wantsVp?.[t];
      if (wv) demandVp[t] += wv;
    }
  }
  if (traitKey) {
    const ti = TRAIT_INFO[traitKey];
    for (const t of THEMES) {
      if (ti.wants?.[t]) demand[t] += ti.wants[t] ?? 0;
      if (ti.wantsVp?.[t]) demandVp[t] += ti.wantsVp[t] ?? 0;
    }
  }
  // состояние игрока — тоже «поставка» тем
  supply['ddk'] += realDdk;
  supply['loser'] = p.loser ? 1 : 0;
  supply['chips'] += p.chips;
  cids.sort(); // канонический порядок: результат не зависит от расположения карт по зонам
  const circus = cids.includes('tsirk-bratev-loshashnykh');
  const legendsOwned = owned.filter((x) => H.isLegendFor(p, x)).length;
  const prof: DeckProfile = {
    n, avg: 0, avgPower: powerSum / n, permPerTurn, turnPower: 0, supply, demand, demandVp,
    ddk: p.ddk.length + p.permanents.filter((x) => hasTag(x, 'deadWizard')).length, realDdk, loser: p.loser, circus,
    chips: p.chips, legendsOwned, traitKey, players: G.players.length, cids, hand: handSizeOf(G), boss: p.boss ?? null,
  };
  // Похотливый маячок: +1 мощь за жетон каждый ход
  if (cids.includes('pohotlivyj-mayachok')) prof.permPerTurn += prof.ddk;
  prof.avg = Math.max(0.3, prof.avgPower);
  let sum = 0;
  for (const x of owned) {
    const def = card(x);
    if (def.permanent) continue;
    sum += cardValue(def, prof).p;
  }
  prof.avg = sum / n;
  prof.turnPower = handSizeOf(G) * prof.avgPower + prof.permPerTurn;
  return prof;
}

// ---------- ценность карты ----------

export interface Value {
  /** мощь и её эквиваленты за розыгрыш */
  p: number;
  /** очки: звезда, условные ПО и синергии в ПО */
  vp: number;
}

/** Сколько розыгрышей «стоит» постоянка в модели среднего: один раз сыграл — работает каждый ход */
export const PERM_TURNS = 2.5;
/** Ценность 1 урона врагу в P (7 урона ≈ 2,3 мощи; по журналам одиночный средний урон окупается слабо, многоцелевой — хорошо) */
export const DMG_P = 0.33;
/** Чипсина ≈ 0,6 мощи: тратится только на легенды и свойства; чипсинные карты по журналам слабы */
export const CHIP_P = 0.6;
export const VP_P = 3;

/** Ценность карты в контексте колоды: сама по себе + синергии в обе стороны */
export function cardValue(def: CardDef, prof: DeckProfile): Value {
  const info = infoOf(def);
  const n = prof.players;
  let p = def.power;
  let vp = (def.type === 'вялая палочка' ? 0 : def.vp ?? 0) + (info.vpExtra ?? 0);
  if (def.type === 'вялая палочка') vp += prof.cids.includes('viagrus-tvyordyj-lord') ? 1 : RULES.limpPenalty;
  // добор: карта из колоды стоит её средней ценности с надбавкой — больше карт за ход = больше синергий и выбора
  if (info.draw) p += info.draw * 1.1 * Math.max(0.8, prof.avg);
  if (info.chips) p += info.chips * CHIP_P;
  if (info.heal) p += info.heal * 0.12;
  if (info.dmg) {
    const targets = info.all === true ? n - 1 : info.all === 'lr' ? Math.min(2, n - 1) : 1;
    let d = info.dmg * targets * DMG_P;
    if (info.unavoidable) d *= 1.15;
    if (info.dmg >= 10) d *= 1.1; // большой урон чаще убивает
    p += d;
  }
  if (info.vpEnemy) p += info.vpEnemy * VP_P * 0.8 * (info.all === true ? n - 1 : 1);
  if (def.defense) p += DEF_BASE + (info.def ?? 0);
  if (def.permanent) p += (info.perTurn ?? 0) * PERM_TURNS + (info.vpPerTurn ?? 0) * VP_P * 0.6;
  p += info.xtra ?? 0;
  const cu = info.custom?.(prof);
  if (cu) {
    p += cu.p ?? 0;
    vp += cu.vp ?? 0;
  }
  // синергии: карта хочет тему, которой колода снабжена
  const hs = handScale(def, prof.n);
  for (const t of THEMES) {
    const w = info.wants?.[t];
    const wv = info.wantsVp?.[t];
    if (!w && !wv) continue;
    let s = prof.supply[t];
    const cap = info.caps?.[t];
    if (cap != null) s = Math.min(s, cap);
    if (w) p += w * s * (THEME_SCOPE[t] === 'hand' && !def.permanent ? Math.min(1, hs * 1.25) : 1);
    if (wv) vp += wv * s;
  }
  // карта даёт тему, которую колода хочет
  const prov = providesOf(def, null);
  for (const t of THEMES) {
    const v = prov[t];
    if (!v) continue;
    const scale = THEME_SCOPE[t] === 'hand' ? hs : 1;
    p += v * scale * prof.demand[t];
    vp += v * prof.demandVp[t];
  }
  // ручные пары
  for (const other of prof.cids) {
    const pv = pairValue(def.id, other);
    if (pv) p += pv * (def.permanent ? 1 : hs);
  }
  if (prof.boss) {
    const b = bossCardBonus(def, info, prof.boss, n);
    p += b.p;
    vp += b.vp;
  }
  return { p, vp };
}

/**
 * Пассивка босса глазами его покупок: что дороже стоит именно ему. Скидки (Хрючево, Яжмужик, палочки Нетотдеда)
 * бот видит через цену (priceFor), здесь — сила карты: урон палочек, чипсины за удар, ПО за легенды, вялые палочки.
 */
export function bossCardBonus(def: CardDef, info: CardInfo, boss: BossId, players: number): Value {
  const ps = BOSSES[boss].passive;
  let p = 0;
  let vp = 0;
  const targets = info.all === true ? players - 1 : info.all === 'lr' ? Math.min(2, players - 1) : 1;
  if (ps.wandDamageBonus && def.tags.includes('wand') && info.dmg) p += ps.wandDamageBonus * targets * DMG_P;
  if (ps.chipsOnHit && def.attack && info.dmg) p += ps.chipsOnHit * targets * CHIP_P * 0.8;
  if (ps.vpPerLegend && def.legend) vp += ps.vpPerLegend;
  if (ps.limpNoPenalty && def.type === 'вялая палочка') vp -= RULES.limpPenalty;
  return { p, vp };
}

/** Ценность защиты карты (то, что теряется, если разыграть её в свой ход, а не защититься) */
export function defenseValue(def: CardDef): number {
  return def.defense ? DEF_BASE + (INFO[def.id]?.def ?? 0) : 0;
}

/** Ценность карты при розыгрыше в свой ход — без защитной составляющей */
export function playValue(def: CardDef, prof: DeckProfile): number {
  return cardValue(def, prof).p - defenseValue(def);
}

/** Буква синергии по числу P (для отчёта) */
export function tierOf(v: number): Tier | null {
  if (v >= TIER_VALUE.S) return 'S';
  if (v >= TIER_VALUE.A) return 'A';
  if (v >= TIER_VALUE.B) return 'B';
  if (v >= TIER_VALUE.C) return 'C';
  if (v >= TIER_VALUE.D) return 'D';
  return null;
}

/**
 * Парная синергия двух карт «в вакууме» (для отчёта и ручной проверки): насколько ценнее A, когда в колоде из 12 карт
 * есть B (и наоборот), сверх их одиночной ценности. Не используется ботом напрямую — бот считает по профилю колоды.
 */
export function pairSynergy(a: CardDef, b: CardDef): number {
  const base = (def: CardDef, other: CardDef | null): number => {
    const prof = syntheticProfile(other ? [other] : []);
    return cardValue(def, prof).p + cardValue(def, prof).vp * VP_P;
  };
  const ab = base(a, b) - base(a, null);
  const ba = base(b, a) - base(b, null);
  return ab + ba;
}

/** Профиль «стартовая колода + указанные карты» (без состояния партии) */
export function syntheticProfile(extra: CardDef[]): DeckProfile {
  const n = 10 + extra.length;
  const supply = zeroThemes();
  const demand = zeroThemes();
  const demandVp = zeroThemes();
  const cids: CardId[] = [];
  for (const def of extra) {
    const info = infoOf(def);
    cids.push(def.id);
    const hs = handScale(def, n);
    const prov = providesOf(def, null);
    for (const t of THEMES) {
      const v = prov[t];
      if (v) supply[t] += THEME_SCOPE[t] === 'hand' ? v * hs : v;
      const w = info.wants?.[t];
      if (w) demand[t] += THEME_SCOPE[t] === 'hand' && !def.permanent ? w * hs : w;
      const wv = info.wantsVp?.[t];
      if (wv) demandVp[t] += wv;
    }
  }
  supply['wand'] += handScale(card('syrnaya-palochka'), n);
  return {
    n, avg: 1, avgPower: 0.7, permPerTurn: 0, turnPower: 3.5, supply, demand, demandVp, ddk: 0, realDdk: 0, loser: false, circus: false,
    chips: 0, legendsOwned: extra.filter((d) => d.legend).length, traitKey: null, players: 3, cids, hand: RULES.handSize, boss: null,
  };
}

/** Карты, которыми можно владеть (для отчёта и проверки полноты таблицы) */
export function ownableCards(): CardDef[] {
  return Object.values(INFO).length ? Object.keys(INFO).map((id) => card(id)) : [];
}

/**
 * Знания ботов под режим ребаланса: правленые карты стоят ботам иначе. Запись — поля CardInfo, которые
 * в режиме заменяют базовые (поле целиком: wants/caps/provides перезаписываются, не сливаются).
 * Применяется/снимается хуком из applyRebalance вместе с ценами; кэш профилей при этом сбрасывается.
 * Числа — из лаборатории (scripts/variant-lab.ts) при подборе правок.
 */
export const INFO_REBALANCE: Partial<Record<CardId, Partial<CardInfo>>> = {
  /** без ежеходной палочки и +2 мощи за палочку вместо +3: мощь только с покупных/чужих палочек */
  'viagrus-tvyordyj-lord': { perTurn: 0.7, vpPerTurn: 0.3, wants: { limp: 1 }, wantsVp: { limp: 2 } },
  /** кап 8 ПО = четыре легенды (6 был перенерфом −6.0%, 10 — недонерфом +5.4%) */
  'gusynya-koroleva-mat': { caps: { legendOwned: 4 } },
  /** дохляки не считаются жетонами: друг друга и Некрошест не кормят */
  'zak-dohlyak': { provides: {} },
  'chak-dohlyak': { provides: {} },
  'zhak-dohlyak': { provides: {} },
  'ermak-dohlyak': { provides: {} },
  'isaak-dohlyak': { provides: {} },
  'nekroshest': { wants: { ddk: 1.5 } },
  'nadyozhnyj-plan': { draw: 1 },
  'zolotoj-parashyut': { def: 0.6 },
  'zad-v-budushchee': { draw: 1 },
  'nekrofeya-gnilyusya': { wants: { ddk: 4.5 } },
  /** защита даёт 1 карту и 1 чипсину вместо 2 и 2 */
  legendohranitel: { draw: 2, chips: 2, def: DEF_DRAW + 0.6 },
  'tsirk-bratev-loshashnykh': { wantsVp: { loser: 11 } },
  'malchik-kotoryj-vyzhral': { xtra: 1 },
};
const infoOrig = new Map<CardId, CardInfo>();
registerModeHook((on) => {
  for (const [id, patch] of Object.entries(INFO_REBALANCE)) {
    if (!patch) continue;
    if (!infoOrig.has(id)) infoOrig.set(id, INFO[id] ?? {});
    const base = infoOrig.get(id) ?? {};
    INFO[id] = on ? { ...base, ...patch } : base;
  }
  // профили колод считались с прежними знаниями
  PROFILE_CACHE.clear();
});

export { isLegend };
